import {
    PROPERTY_TYPE,
    TAddressMdl,
    TFeature,
    TPropertyBaseMdl,
    TPropertyListingMdl,
    TPropertyPage,
    TUnitsDetails,
} from "properties/_models/PropertyMdl";
import _ from "lodash";
import { GALLERY_TABS, PROPERTY_OPTION } from "_configs/propertyConfig";
import { URLS } from "_configs/URLS";
import { reformatStringForUrls } from "_common/_utils/alphaNumUtils";
import { convertPrices, formatPesosPrice, formatPrice } from "_common/_utils/currencyUtils";
import { CURRENCY_OPTIONS, PRICE_CONVERSION } from "_common/currency/CurrencyTypes";
import i18next from "i18next";
import { TCompare } from "properties/compare/_models/properties-favorite.model";
import { IUnitModel } from "api/units/_models/UnitModel";

export function getParentGeoZoneByProperty(property: { address: TAddressMdl }, lang?: string) {
    return URLS.buy(
        lang,
        reformatStringForUrls(property.address.provinceShort),
        reformatStringForUrls(property.address.city),
        property.address.neighbourhood ? reformatStringForUrls(property.address.neighbourhood) : undefined,
    );
}

export function flattenObject(
    baseObject?: any,
    path = "",
    output: {
        [key: string]: string | number | undefined | null | boolean;
    } = {},
) {
    if (
        baseObject === undefined ||
        baseObject === null ||
        typeof baseObject !== "object" ||
        _.isArray(baseObject) ||
        _.isDate(baseObject)
    ) {
        output[path] = baseObject;
    } else {
        for (const key in baseObject) {
            flattenObject(baseObject[key], path !== "" ? path + "." + key : key, output);
        }
    }
    return output;
}

export function reformatImagesForGallery(item: TPropertyPage) {
    if (item.photos?.length > 3 && item.photos[0].url !== item.photos[2].url) {
        const title = item?.video ? GALLERY_TABS.VIDEO : GALLERY_TABS.GALLERY;
        item.photos.splice(2, 0, { ...item.photos[0], title: title });
        if (item?.video || item.floorPlans.length > 0) {
            const title = item?.video ? GALLERY_TABS.GALLERY : GALLERY_TABS.LEVEL_PLAN;
            item.photos.splice(4, 0, { ...item.photos[0], title: title });
        }
        if (item?.video && item.floorPlans.length > 0) {
            const title = GALLERY_TABS.LEVEL_PLAN;
            item.photos.splice(6, 0, { ...item.photos[0], title: title });
        }
    }
    return item;
}

export function isLandOrCommercial(propertyType?: PROPERTY_TYPE) {
    if (!propertyType) return false;
    return propertyType === PROPERTY_TYPE.land || propertyType === PROPERTY_TYPE.commercial;
}

export function isInPesosProperty(property: Pick<TPropertyListingMdl, "features">): boolean {
    return !!property?.features?.[PROPERTY_OPTION.closingInPesos];
}

export function isLuxury(property: Pick<TPropertyListingMdl, "features">): boolean {
    return !!property?.features?.[PROPERTY_OPTION.luxury];
}

export function calculateAndFormatPropertyPrice(
    property: {
        priceMin: number;
        priceInPesosMin?: number;
        priceIsInPesos?: boolean;
    },
    currencyDisplay: CURRENCY_OPTIONS,
) {
    const { priceMin, priceInPesosMin, priceIsInPesos } = property;
    const minPrice = priceIsInPesos ? priceInPesosMin || 0 : priceMin || 0;
    switch (currencyDisplay) {
        case CURRENCY_OPTIONS.MXN:
            return priceInPesosMin
                ? formatPrice(priceInPesosMin, false, false, CURRENCY_OPTIONS.MXN)
                : formatPrice(
                      convertPrices(priceMin, PRICE_CONVERSION.USD_TO_PESOS),
                      false,
                      false,
                      CURRENCY_OPTIONS.MXN,
                  );
        case CURRENCY_OPTIONS.USD:
            return formatPrice(priceMin ?? 0);
        case CURRENCY_OPTIONS.CAD:
            return formatPrice(
                convertPrices(minPrice, priceIsInPesos ? PRICE_CONVERSION.PESOS_TO_CAD : PRICE_CONVERSION.USD_TO_CAD),
                false,
                false,
                CURRENCY_OPTIONS.CAD,
            );
        case CURRENCY_OPTIONS.EUR:
            return formatPrice(
                convertPrices(minPrice, priceIsInPesos ? PRICE_CONVERSION.PESOS_TO_EUR : PRICE_CONVERSION.USD_TO_EUR),
                false,
                false,
                CURRENCY_OPTIONS.EUR,
            );
        default:
            return "";
    }
}

export function getUnitsDetails(availableUnits: IUnitModel[]): TUnitsDetails[] {
    const unitsDetailsMap: { [key: number]: TUnitsDetails } = {};

    availableUnits.forEach((unit) => {
        const bedroomCount = unit.bedrooms;
        const price = unit.price;

        if (!unitsDetailsMap[bedroomCount] || price < unitsDetailsMap[bedroomCount].price) {
            unitsDetailsMap[bedroomCount] = { bedroom: bedroomCount, price: price };
        }
    });

    return Object.values(unitsDetailsMap);
}

export const formatBedroomsNumber = (property: TPropertyPage | TCompare, min: number, max = 0) => {
    const formatNumber = (num: number) => (num === 0 ? i18next.t("propertyPage.header.studio") : num);

    if (min === 0 && max === 0) {
        if (property.nbOfUnitsAvailable > 1) {
            return i18next.t("propertyPage.noBedUnits");
        } else {
            return i18next.t("propertyPage.noBedUnit");
        }
    }

    return min === max
        ? i18next.t("propertyPage.bedUnit", { unit: formatNumber(min) })
        : i18next.t("propertyPage.bedUnits", {
              unit: Array.from({ length: max - min + 1 }, (_, i) => formatNumber(i + min))
                  .join(", ")
                  .replace(/, ([^,]*)$/, " & $1"),
          });
};

export const formatBathroomsNumber = (min: number, max = 0) => {
    if (min === 0 && max === 0) {
        return i18next.t("propertyPage.noBathroomUnit");
    }

    return min === max
        ? i18next.t("propertyPage.bathroomUnit", { unit: min })
        : i18next.t("propertyPage.bathroomUnits", {
              unit: Array.from({ length: max - min + 1 }, (_, i) => i + min)
                  .join(", ")
                  .replace(/, ([^,]*)$/, " & $1"),
          });
};

export const getCompleteAddresse = (item: TPropertyBaseMdl) => {
    if (!item) return "";
    const sortAddress = [
        item.address.neighbourhood,
        item.address.city,
        item.address.province,
        i18next.t("propertyPage.country." + (item.address.country ?? "MEX")),
    ];
    return sortAddress
        .filter((addressPart) => addressPart !== undefined && addressPart !== "")
        .slice(0)
        .join(", ");
};

export function getUnitAndPropertyFeatures(propertyFeatures: TFeature): { [key in PROPERTY_OPTION]?: boolean } | null {
    const propertyFeatureKeys: PROPERTY_OPTION[] = [
        PROPERTY_OPTION.appliances,
        PROPERTY_OPTION.automatedBlinds,
        PROPERTY_OPTION.blinds,
        PROPERTY_OPTION.muralAirConditioning,
        PROPERTY_OPTION.washerDryerInSuite,
        PROPERTY_OPTION.furnishedEquipped,
    ];

    const matchingFeatures: { [key in PROPERTY_OPTION]?: boolean } = {};

    propertyFeatureKeys.forEach((feature) => {
        if (feature in propertyFeatures && propertyFeatures[feature]) {
            matchingFeatures[feature] = true;
        }
    });

    return Object.keys(matchingFeatures).length > 0 ? matchingFeatures : null;
}

export function getPriceOfProperty(item: TPropertyPage) {
    if (item.priceIsNotDisplayed) {
        return i18next.t("propertiesPage.card.priceOnRequest");
    }

    return (
        i18next.t("words.from") +
        " " +
        (isInPesosProperty(item)
            ? formatPesosPrice(item.priceInPesos.min, false, isInPesosProperty(item))
            : formatPrice(item.price.min))
    );
}
